/**
 * authForm — shared court-exhibit form primitives for the hand-built auth pages
 * (accountant login/register, attorney reset). Mono-caps labels, hairline inputs
 * (no fill, focus drives the rule to full ink), one squared orange submit button,
 * a ruled error notice. Matches the Clerk-form styling in clerkAppearance.ts and
 * the homepage exhibit system. Token-only.
 */

import type { ChangeEvent, ReactNode } from 'react';

const LABEL = 'mb-1.5 block font-mono text-[11px] uppercase tracking-[0.08em] text-text-secondary';
const INPUT =
  'h-11 w-full rounded-none border border-[var(--color-border)] bg-transparent px-3 font-body text-sm ' +
  'text-text-primary placeholder:text-text-dim transition-colors focus:border-[var(--color-text-primary)] focus:outline-none';

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  name,
  required,
  placeholder,
  autoComplete,
  testId,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  testId?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testId}
        className={INPUT}
      />
    </div>
  );
}

/** Hairline textarea, styled to match AuthField. */
export function AuthTextarea({
  id,
  label,
  name,
  value,
  onChange,
  required,
  placeholder,
  rows = 6,
}: {
  id: string;
  label: string;
  name?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        rows={rows}
        className="w-full resize-none rounded-none border border-[var(--color-border)] bg-transparent px-3 py-2.5 font-body text-sm text-text-primary placeholder:text-text-dim transition-colors focus:border-[var(--color-text-primary)] focus:outline-none"
      />
    </div>
  );
}

/** Hairline select, styled to match AuthField. */
export function AuthSelect({
  id,
  label,
  name,
  value,
  onChange,
  required,
  children,
}: {
  id: string;
  label: string;
  name?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="h-11 w-full rounded-none border border-[var(--color-border)] bg-transparent px-3 font-body text-sm text-text-primary transition-colors focus:border-[var(--color-text-primary)] focus:outline-none"
      >
        {children}
      </select>
    </div>
  );
}

/** Squared orange mono-caps submit — the one primary action on the form. */
export function AuthSubmit({
  loading,
  children,
  loadingText = 'Working…',
}: {
  loading?: boolean;
  children: ReactNode;
  loadingText?: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex h-11 w-full items-center justify-center gap-2 rounded-none bg-brand font-mono text-xs font-semibold uppercase tracking-[0.14em] transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
      style={{ color: 'var(--color-on-brand)' }}
    >
      {loading ? (
        <>
          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {loadingText}
        </>
      ) : (
        children
      )}
    </button>
  );
}

/** Ruled error notice — a left orange rule + mono text, no fill, no rounded. */
export function AuthError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="alert"
      className="border-l-2 py-2 pl-3 font-mono text-[11px] leading-relaxed text-brand-text"
      style={{ borderColor: 'var(--color-brand)' }}
    >
      {children}
    </div>
  );
}

/** Neutral ruled notice (e.g. invite-required, info) — hairline rule, mono/sans. */
export function AuthNotice({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="border-l-2 py-2 pl-3" style={{ borderColor: 'var(--color-border-strong)' }}>
      {title && (
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-text-secondary">{title}</p>
      )}
      <p className="mt-1 font-body text-[13px] leading-relaxed text-text-muted">{children}</p>
    </div>
  );
}
